import { HttpParams } from '@angular/common/http';
import {
    Component,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { Gruppe } from '../classes/gruppe';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../services/message-service/message.service';
import { KieneUtils } from '../../../utils/KieneUtils';
import { AbgangListeComponent } from './abgang-liste/abgang-liste.component';
import { AbgangslisteSchliessenDialogComponent } from './abgangsliste-schliessen-dialog/abgangsliste-schliessen-dialog.component';
import { GruppeAbgangService } from './gruppe-abgang.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { HIT_LISTE_WIEDER_OEFFNEN_FEHLER } from '../../gruppe-tier/hit-meldung-status.util';
import { ohneTierlisteKommentar, Tierliste } from '../../gruppe-tier/classes/tierliste';
import { GruppenService } from '../gruppen.service';
import { ConfirmDialogInput, ConfirmDialogResult } from '../../dialogs/confirm-dialog/confirm-dialog';
import { TierlistenauswahlDialogComponent } from './tierlistenauswahl-dialog/tierlistenauswahl-dialog.component';
import { EnterValueInput, EnterValueResponse } from '../../dialogs/enter-value-dialog/enter-value-response';
import { EnterValueDialogComponent } from '../../dialogs/enter-value-dialog/enter-value-dialog.component';
import { AusstallenService } from '../../../components/ausstallen/ausstallen.service';
import { GruppeTier } from '../../../components/gruppe-tier/classes/gruppe-tier';
import { AusstallenDialogInput, AusstallenDialogResult } from '../../ausstallen/ausstallen-dialog/ausstallen-dialog';
import { TierlistenauswahlDialogInput } from './tierlistenauswahl-dialog/tierlistenauswahl-dialog';
import {
    getTierlisteZielbetriebAnzeigeName,
    getTierlisteZielbetriebAnzeigeZusatz,
} from '../../gruppe-tier/tierliste-zielbetrieb-display.util';

@Component({
    selector: 'kiene-gruppe-abgang',
    templateUrl: './gruppe-abgang.component.html',
    styleUrls: ['./gruppe-abgang.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class GruppeAbgangComponent implements OnInit, OnDestroy {
    protected readonly KieneUtils = KieneUtils;

    @ViewChild('abgangListe') abgangListe?: AbgangListeComponent;
    @ViewChild('tierlisteSelect') tierlisteSelect: MatSelect;
    @Input() gruppe: Gruppe;
    @Input() alsDialog = false;
    @Input() tierliste: Tierliste;
    @Output() geschlossen = new EventEmitter<Tierliste>();
    @Output() tiereAnzahlChanged = new EventEmitter<number>();

    gruppe_id: number;
    tiere: GruppeTier[] = [];
    tierlisteId: string;
    tierlisten: Tierliste[] = [];
    ladeAbgaengeSub: Subscription;
    ladeTierlistenSub: Subscription;
    aktuelleTierlisteSubscription: Subscription;
    gruppeSubscription: Subscription;

    ladeTiereFuerGruppeSub: Subscription;

    isLoading = false;
    currentTabIndex = 0;
    /** Kundenportal phone route: scan + number pad + list tabs. */
    showMobileTabs = false;
    anzahlTiereInListe = 0;

    @HostBinding('class.gruppe-abgang--mobile-page')
    get mobilePageLayout(): boolean {
        return this.showMobileTabs;
    }

    constructor(
        private readonly location: Location,
        private readonly route: ActivatedRoute,
        private readonly abgangService: GruppeAbgangService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl,
        private readonly gruppenService: GruppenService,
        private readonly api: KieneBackendApiService,
        private readonly dialog: MatDialog,
        private readonly messageService: MessageService,
        private readonly ausstallenService: AusstallenService,
        private readonly localStorageService: LocalStorageService,
    ) {
        if (this.gruppe) {
            this.gruppe_id = this.gruppe.gruppe_id;
        } else {
            const id = this.route.snapshot.paramMap.get('gruppe_id');
            if (id) {
                this.gruppe_id = Number(id);
            }
        }
        this.tierlisteId = this.route.snapshot.queryParamMap.get('tierliste_id');
    }

    goBack(): void {
        this.location.back();
    }

    tabChanged(event: MatTabChangeEvent): void {
        this.currentTabIndex = event.index;
    }

    getZielbetriebAnzeigeName(): string {
        return getTierlisteZielbetriebAnzeigeName(this.tierliste);
    }

    getZielbetriebAnzeigeZusatz(): string {
        return getTierlisteZielbetriebAnzeigeZusatz(this.tierliste);
    }

    /** kunde_id for tierliste reads (darf_wieder_oeffnen depends on selected Betrieb). */
    private tierlisteReadParams(tierlisteId: number | string): HttpParams {
        let params = new HttpParams().set('tierliste_id', String(tierlisteId));
        const kundeId =
            Number(this.gruppe?.kunde_id) || Number(this.localStorageService.getCurrentClient()?.kunde_id) || 0;
        if (kundeId > 0) {
            params = params.set('kunde_id', String(kundeId));
        }
        return params;
    }

    ngOnDestroy(): void {
        this.aktuelleTierlisteSubscription?.unsubscribe();
        this.ladeAbgaengeSub?.unsubscribe();
        this.gruppeSubscription?.unsubscribe();
        this.ladeTierlistenSub?.unsubscribe();
    }

    ngOnInit(): void {
        this.showMobileTabs = !this.alsDialog;

        if (this.tierliste) {
            if (this.tierliste.gruppe_id_abgang) {
                this.gruppe_id = this.tierliste.gruppe_id_abgang;
            }
            this.abgangService.setAbgangsliste(this.tierliste, true, true);
        }

        if (this.tierlisteId) {
            this.api
                .getByIdAlternative(
                    this.apiBaseUrl + 'tierliste/read_one_kundenportal.php',
                    'tierliste_id',
                    this.tierlisteId,
                    this.tierlisteReadParams(this.tierlisteId),
                )
                .subscribe({
                    next: response => {
                        this.tierliste = response;
                        const gruppeIdAbgang = Number(response?.gruppe_id_abgang);
                        if (gruppeIdAbgang > 0) {
                            this.gruppe_id = gruppeIdAbgang;
                            this.gruppeSubscription?.unsubscribe();
                            this.gruppeSubscription = this.ladeGruppe(gruppeIdAbgang).subscribe({
                                next: gruppe => {
                                    if (gruppe) {
                                        this.gruppe = gruppe;
                                    }
                                },
                                error: err => {
                                    this.messageService.errorMessage('Fehler!', err);
                                },
                            });
                        }
                        this.abgangService.setAbgangsliste(this.tierliste, false, true);
                    },
                    error: err => {
                        this.messageService.errorMessage('Fehler!', err);
                    },
                });
        }

        if (this.gruppe_id) {
            this.gruppeSubscription?.unsubscribe();
            this.gruppeSubscription = this.ladeGruppe(this.gruppe_id).subscribe({
                next: gruppe => {
                    if (gruppe) {
                        this.gruppe = gruppe;
                    }
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
        }

        this.ladeTierlisten();

        this.abgangService.watchAbgangsliste().subscribe({
            next: liste => {
                this.tierliste = liste.tierliste;
                if (this.tierliste?.anzahl_tiere != null) {
                    this.anzahlTiereInListe = Number(this.tierliste.anzahl_tiere);
                }
                if (liste.reloadGruppenTiere) {
                    this.ladeTiere();
                }
                if (liste.reloadAbgangsliste) {
                    this.ladeAktuelleTierliste();
                }
            },
        });

        this.abgangService.watchAlleTiereHinzugefuegt().subscribe({
            next: alleHinzugefuegt => {
                if (alleHinzugefuegt) {
                    this.abgangListe?.ladeTiereFuerAbgangsliste('alleTiereHinzugefuegt');
                }
            },
        });

        this.ladeTiere();
    }

    ladeAktuelleTierliste() {
        if (!this.tierliste) {
            return;
        }
        const params = this.tierlisteReadParams(this.tierliste.tierliste_id);
        this.aktuelleTierlisteSubscription?.unsubscribe();
        this.aktuelleTierlisteSubscription = this.api
            .get(this.apiBaseUrl + 'tierliste/read_one_kundenportal.php', params)
            .subscribe({
                next: response => {
                    this.tierliste = response;
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
    }

    ladeGruppe(gruppeId: number): Observable<Gruppe> {
        if (!gruppeId) {
            return EMPTY;
        }
        const params = new HttpParams().set('gruppe_id', gruppeId?.toString());
        return this.api.get(this.apiBaseUrl + 'gruppe/read_one_kundenportal.php', params);
    }

    ladeTierlisten(liste?: Tierliste) {
        this.ladeTierlistenSub?.unsubscribe();
        this.ladeTierlistenSub = this.gruppenService.ladeTierlistenHistorie(this.gruppe_id).subscribe({
            next: response => {
                this.tierlisten = response.records;
                if (this.tierlisteId || this.tierliste) {
                    return;
                }
                if (this.tierlisten.length === 1) {
                    this.tierliste = this.tierlisten[0];
                    this.abgangService.setAbgangsliste(this.tierlisten[0], true, true);
                } else if (this.tierlisten.length < 1) {
                    this.abgangService.setAbgangsliste(undefined, true, true);
                } else if (this.tierlisten.length > 1 && !liste) {
                    let alleListenGeschlossen = true;
                    for (const tl of this.tierlisten) {
                        if (KieneUtils.apiStatusId(tl.status_id, 161)) {
                            alleListenGeschlossen = false;
                            break;
                        }
                    }
                    if (!alleListenGeschlossen) {
                        this.openTierlistenauswahlDialog();
                    }
                } else if (liste) {
                    this.tierliste = this.tierlisten.find(tl => {
                        return tl.tierliste_id === liste.tierliste_id;
                    });
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    neueTierlisteErstellen() {
        this.ausstallenService.ausstallen(this.gruppe, false, undefined);
    }

    aktuelleTierlisteEditieren() {
        const extern = KieneUtils.isApiOne(this.tierliste?.tierliste_extern);
        const intern = !extern; // Interne Tierlisten haben tierliste_extern = 0
        // Für die Bearbeitung setzen wir intern basierend auf tierliste_extern
        const input = new AusstallenDialogInput(intern, extern, { tierliste: this.tierliste });
        input.tierliste = this.tierliste;
        this.ausstallenService.openAusstallenDialog(input, {
            onComplete: (result: AusstallenDialogResult) => {
                if (result?.tierliste) {
                    this.tierliste = result.tierliste;
                    this.ladeTierlisten(result.tierliste);
                    this.abgangService.setAbgangsliste(result.tierliste, true, true);
                }
            },
        });
    }

    aktuelleTierlisteLoeschen() {
        const input = new ConfirmDialogInput();
        input.headline = 'Liste löschen?';
        input.text = 'Wollen Sie die aktuelle Ausstallliste wirklich löschen?';
        input.okButton = 'Löschen';
        input.buttonColor = 'warn';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    this.api
                        .post(this.apiBaseUrl + 'tierliste/delete_kundenportal.php', this.tierliste, undefined)
                        .subscribe({
                            next: () => {
                                this.messageService.infoMessage('Die Ausstallliste wurde erfolgreich gelöscht!');
                                this.ladeTierlisten();
                                this.abgangService.setAbgangsliste(undefined, true, true);
                            },
                            error: err => {
                                this.messageService.errorMessage('Fehler!', err);
                            },
                        });
                }
            },
        });
    }

    openTierlistenauswahlDialog() {
        if (this.tierlisten?.length === 0) {
            return;
        }
        const input = new TierlistenauswahlDialogInput();
        input.gruppe = this.gruppe;
        input.tierlisten = this.tierlisten;
        this.dialog
            .open<TierlistenauswahlDialogComponent, TierlistenauswahlDialogInput, Tierliste>(
                TierlistenauswahlDialogComponent,
                { maxWidth: '100vw', maxHeight: '80vh', width: '100%', data: input },
            )
            .afterClosed()
            .subscribe({
                next: tierliste => {
                    if (tierliste) {
                        this.ladeTierlisten(tierliste);
                        this.tierliste = tierliste;
                        this.abgangService.setAbgangsliste(tierliste, true, true);
                    }
                },
            });
    }

    aktuelleTierlisteWiederOeffnen() {
        if (this.tierliste) {
            const statusAlt = this.tierliste.status_id;
            this.tierliste.status_id = 161;
            this.api.post(
                this.apiBaseUrl + 'tierliste/update_kundenportal.php',
                ohneTierlisteKommentar(this.tierliste),
                null,
            ).subscribe({
                next: (response: Tierliste) => {
                    this.messageService.infoMessage('Liste erfolgreich geöffnet!');
                    this.ladeTierlisten(response);
                    this.abgangService.setAbgangsliste(response, false, false);
                },
                error: err => {
                    this.tierliste.status_id = statusAlt;
                    this.messageService.errorMessage(HIT_LISTE_WIEDER_OEFFNEN_FEHLER, err);
                    this.ladeAktuelleTierliste();
                },
            });
        }
    }

    aktuelleTierlisteTeilen() {
        if (this.tierliste) {
            const input = new EnterValueInput();
            input.title = 'Tierliste freigeben?';
            input.text =
                'Die Email-Adresse des Benutzers angeben, für den der Zugriff auf diese Liste erlaubt werden soll.';
            input.okButton = 'freigeben';
            input.buttonColor = 'accent';
            input.addInputValue('Email Adresse', 'text');

            const dialogRef = this.dialog.open(EnterValueDialogComponent, { data: input, position: { top: '10px' } });
            dialogRef.afterClosed().subscribe({
                next: (res: EnterValueResponse) => {
                    if (res?.result === true) {
                        this.tierlisteTeilen(this.tierliste, res?.getValue(0));
                    }
                },
            });
        }
    }

    tierlisteTeilen(liste: Tierliste, email: string) {
        this.api
            .post(this.apiBaseUrl + 'tierliste/teilen.php', { tierliste_id: liste?.tierliste_id, email: email }, null)
            .subscribe({
                next: () => {
                    this.messageService.infoMessage('Die Liste wurde erfolgreich freigegeben!');
                },
                error: err => {
                    this.messageService.errorMessage('Fehler bei Freigabe!', err);
                },
            });
    }

    ladeTiere() {
        if (this.gruppe_id) {
            let params = new HttpParams().set('abgaenge', '0');
            params = params.set('tot', '0');
            this.ladeTiereFuerGruppeSub?.unsubscribe();
            this.ladeTiereFuerGruppeSub = this.gruppenService.ladeGruppenTiere(this.gruppe_id, params).subscribe({
                next: tiere => {
                    this.isLoading = false;
                    this.tiere = tiere.records;
                },
                error: err => {
                    this.isLoading = false;
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
        }
    }

    alleTiereZuAbgangslisteHinzufuegen() {
        if (!this.tierliste || this.tiere.length === 0) {
            return;
        }
        this.isLoading = true;
        const obj = {
            tierliste_id: this.tierliste.tierliste_id,
            gruppen_tiere: this.tiere,
            gruppe_id: this.gruppe_id ?? undefined,
        };
        this.api.post(this.apiBaseUrl + 'tierliste/abgang_kundenportal.php', obj, undefined).subscribe({
            next: (tierlisteAntwort: Tierliste) => {
                this.messageService.infoMessage(
                    this.tiere.length + ' Tiere wurden zur aktuellen Ausstallliste hinzugefügt.',
                );
                this.isLoading = false;
                this.abgangService.setAbgangsliste(tierlisteAntwort, true, true);
                // Gruppentier-Liste auf der Seite neu laden, von der aus der Dialog geöffnet wurde
                if (this.gruppe_id) {
                    this.abgangService.gruppeAktualisieren(this.gruppe_id);
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
                this.isLoading = false;
            },
        });
    }

    onTiereAnzahlChanged(anzahl: number): void {
        this.anzahlTiereInListe = anzahl;
        this.tiereAnzahlChanged.emit(anzahl);
    }

    canShowMobileListeAbschliessen(): boolean {
        if (!this.showMobileTabs || this.alsDialog || !this.tierliste || !KieneUtils.apiStatusId(this.tierliste.status_id, 161)) {
            return false;
        }
        if (KieneUtils.isApiOne(this.tierliste.mehrere_gruppen)) {
            return KieneUtils.sameId(
                this.tierliste.erstellt_von,
                this.localStorageService.getCurrentUser().benutzer_id,
            );
        }
        return true;
    }

    isMobileListeAbschliessenDisabled(): boolean {
        const anzahl = this.anzahlTiereInListe || Number(this.tierliste?.anzahl_tiere ?? 0);
        return anzahl <= 0;
    }

    listeAbschliessen(): void {
        if (this.showMobileTabs && this.tierliste?.tierliste_id) {
            if (this.ausstallenService.tryOpenMobileAusstallenFinishPage(this.tierliste, this.gruppe)) {
                return;
            }
        }
        if (this.abgangListe) {
            this.abgangListe.abgangslisteSchliessen();
            return;
        }
        if (!this.tierliste?.tierliste_id) {
            return;
        }
        const dialogRef = this.dialog.open(AbgangslisteSchliessenDialogComponent, {
            maxWidth: '95vw',
            maxHeight: '95vh',
            data: { tierliste: this.tierliste, alsDialog: this.alsDialog },
            disableClose: true,
        });
        dialogRef.afterClosed().subscribe({
            next: result => {
                if (result) {
                    this.abgangService.setAbgangsliste(this.tierliste, false, true);
                    this.abgangService.tierListeAbgeschlossen(this.tierliste, 'listeAbschliessen');
                }
            },
        });
    }

    public triggerScansFortsetzen(): void {
        this.abgangListe?.abgangslisteSchliessen();
    }
}
